import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import { fi } from "date-fns/locale";
import { InstallationWithRelations } from "@/types/installations";

/** OrderInstallationsCard käyttää "cemeteries" aliasta */
type OrderInstallation = Omit<InstallationWithRelations, 'cemetery'> & {
  cemeteries?: { id?: string; name?: string } | null;
};

interface OrderInstallationsCardProps {
  orderId: string;
  orderNumber: string;
}

const TASK_TYPE_LABELS: Record<string, string> = {
  installation: "Asennus",
  delivery: "Toimitus",
  maintenance: "Huolto",
  pickup: "Nouto",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Odottaa",
  in_progress: "Käynnissä",
  completed: "Valmis",
  cancelled: "Peruutettu",
  partial: "Osittain",
  skipped: "Ohitettu",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-gray-50 text-gray-700 border-gray-200",
  partial: "bg-orange-50 text-orange-700 border-orange-200",
  skipped: "bg-gray-50 text-gray-500 border-gray-200",
};

export function OrderInstallationsCard({ orderId, orderNumber }: OrderInstallationsCardProps) {
  const { data: installations, isLoading } = useQuery({
    queryKey: ["order-installations", orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("installations")
        .select(`
          id,
          task_type,
          status,
          scheduled_date,
          completed_date,
          notes,
          cemeteries (
            id,
            name
          )
        `)
        .eq("order_id", orderId)
        .is("deleted_at", null)
        .order("scheduled_date", { ascending: true });

      if (error) throw error;
      return data as OrderInstallation[];
    },
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "d.M.yyyy", { locale: fi });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!installations || installations.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Asennustehtävät</CardTitle>
        <CardDescription>
          Tilaukseen liittyvät asennukset ja toimitukset
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tyyppi</TableHead>
              <TableHead>Hautausmaa</TableHead>
              <TableHead>Ajankohta</TableHead>
              <TableHead>Tila</TableHead>
              <TableHead>Muistiinpanot</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {installations.map((installation) => (
              <TableRow key={installation.id}>
                <TableCell>
                  <Badge variant="outline">
                    {TASK_TYPE_LABELS[installation.task_type] || installation.task_type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <p className="font-medium">{installation.cemeteries?.name}</p>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <p className="text-sm">
                      <span className="text-muted-foreground">Suunniteltu: </span>
                      {formatDate(installation.scheduled_date)}
                    </p>
                    {installation.completed_date && (
                      <p className="text-sm">
                        <span className="text-muted-foreground">Valmis: </span>
                        {formatDate(installation.completed_date)}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={STATUS_COLORS[installation.status] || ""}
                  >
                    {STATUS_LABELS[installation.status] || installation.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {installation.notes ? (
                    <p className="text-sm text-muted-foreground max-w-xs truncate">
                      {installation.notes}
                    </p>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
